// Hook for fetching applications — all filtering, search, and sorting are BE-side.

import { useState, useEffect, useCallback } from 'react';
import type { Application, ApplicationQueryParams, PaginatedMeta } from '../types';
import { applicationsService, type ApiError } from '../applications.service';

const DEFAULT_META: PaginatedMeta = {
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
  hasPrevious: false,
  hasNext: false,
};

export interface UseApplicationsResult {
  applications: Application[];
  meta: PaginatedMeta;
  loading: boolean;
  error: ApiError | null;
  refetch: () => void;
}

export function useApplications(params: ApplicationQueryParams): UseApplicationsResult {
  const [page, setPageData] = useState<{ items: Application[]; meta: PaginatedMeta } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const paramsKey = JSON.stringify(params);

  const fetch = useCallback(async () => {
    const parsed = JSON.parse(paramsKey) as ApplicationQueryParams;

    if (!parsed.companyId) {
      setPageData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await applicationsService.getAll(parsed);
      setPageData({ items: result.data, meta: result.meta });
    } catch (err) {
      setError(err as ApiError);
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, [paramsKey]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    applications: page?.items ?? [],
    meta: page?.meta ?? DEFAULT_META,
    loading,
    error,
    refetch: fetch,
  };
}
